import { useState, useEffect, useCallback } from 'react';
import {
  History,
  Trash2,
  Edit2,
  Check,
  X,
  ExternalLink,
  BarChart2,
  CornerUpRight,
  GitCompareArrows,
  Layers,
  MapPin,
  Calendar,
  Disc,
} from 'lucide-react';
import { rpc, RpcError } from '@/api/client';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { useRouter } from '@/context/RouterContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { analysisSchema } from './analysisSchema';
import { SessionRemovedAnalysisDialog } from './SessionRemovedAnalysisDialog';
import type {
  AnalysisRecord,
  AnalysisType,
  ListAnalysesResponse,
  UpdateAnalysisResponse,
  DeleteAnalysisResponse,
} from '@/types/telemetry';

const TYPE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  ANALYSIS_TYPE_SECTOR: BarChart2,
  ANALYSIS_TYPE_CORNER: CornerUpRight,
  ANALYSIS_TYPE_BRAKING: Disc,
  ANALYSIS_TYPE_LAP_DIFF: GitCompareArrows,
  ANALYSIS_TYPE_AGGREGATION: Layers,
  ANALYSIS_TYPE_AGGREGATION_DIFF: GitCompareArrows,
};

const TYPE_ROUTES: Record<string, string> = {
  ANALYSIS_TYPE_SECTOR: '#/analysis/sector',
  ANALYSIS_TYPE_CORNER: '#/analysis/corner',
  ANALYSIS_TYPE_BRAKING: '#/analysis/braking',
  ANALYSIS_TYPE_LAP_DIFF: '#/analysis/lapdiff',
  ANALYSIS_TYPE_AGGREGATION: '#/analysis/aggregation',
  ANALYSIS_TYPE_AGGREGATION_DIFF: '#/analysis/aggregation-diff',
};

function getAnalysisTrack(item: AnalysisRecord): string {
  const track = item.sector?.track || item.corner?.track || item.braking?.track || item.lapDiff?.track || item.aggregation?.track || item.aggregationDiff?.track;
  return track?.trackName || track?.trackId || '';
}

function getAnalysisScopeLabel(item: AnalysisRecord, t: (key: any, params?: any) => string): string {
  if (item.sector?.sessions?.sessionIds) {
    return t('analysis.history.sessionsCount', { n: item.sector.sessions.sessionIds.length });
  }
  if (item.corner?.sessions?.sessionIds) {
    return t('analysis.history.sessionsCount', { n: item.corner.sessions.sessionIds.length });
  }
  if (item.braking?.sessions?.sessionIds) {
    return t('analysis.history.sessionsCount', { n: item.braking.sessions.sessionIds.length });
  }
  if (item.aggregation?.sessions?.sessionIds) {
    return t('analysis.history.sessionsCount', { n: item.aggregation.sessions.sessionIds.length });
  }
  if (item.aggregationDiff) {
    const hasLap = Boolean(item.aggregationDiff.lapA || item.aggregationDiff.lapB);
    if (hasLap) {
      const aLabel = item.aggregationDiff.lapA
        ? `Lap ${item.aggregationDiff.lapA.lapNumber}`
        : t('analysis.history.sessionsCount', { n: item.aggregationDiff.sessionsA?.sessionIds?.length ?? 0 });
      const bLabel = item.aggregationDiff.lapB
        ? `Lap ${item.aggregationDiff.lapB.lapNumber}`
        : t('analysis.history.sessionsCount', { n: item.aggregationDiff.sessionsB?.sessionIds?.length ?? 0 });
      return `${aLabel} vs ${bLabel}`;
    }
    const aCount = item.aggregationDiff.sessionsA?.sessionIds?.length ?? 0;
    const bCount = item.aggregationDiff.sessionsB?.sessionIds?.length ?? 0;
    return t('analysis.history.groupsScope', { a: aCount, b: bCount });
  }
  if (item.lapDiff?.laps?.laps) {
    return t('analysis.history.lapsCount', { n: item.lapDiff.laps.laps.length });
  }
  return '—';
}

function getTypeLabel(type: AnalysisType, t: (key: any) => string): string {
  switch (type) {
    case 'ANALYSIS_TYPE_SECTOR':
      return t('analysis.history.filterSector');
    case 'ANALYSIS_TYPE_CORNER':
      return t('analysis.history.filterCorner');
    case 'ANALYSIS_TYPE_BRAKING':
      return t('analysis.history.filterBraking');
    case 'ANALYSIS_TYPE_LAP_DIFF':
      return t('analysis.history.filterLapDiff');
    case 'ANALYSIS_TYPE_AGGREGATION':
      return t('analysis.history.filterAggregation');
    case 'ANALYSIS_TYPE_AGGREGATION_DIFF':
      return t('analysis.history.filterAggregationDiff');
    default:
      return type;
  }
}

export function AnalysisHistoryPage() {
  const { params, navigate } = useRouter();
  const { t } = useI18n();
  const { showToast } = useToast();

  const page = parseInt(params.page, 10) || 1;
  const query = params.q || '';

  const [analyses, setAnalyses] = useState<AnalysisRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Inline renaming state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  // Deletion state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Session removed dialog state
  const [sessionRemovedAnalysis, setSessionRemovedAnalysis] = useState<AnalysisRecord | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const loadData = useCallback(async (currentPage: number, currentQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await rpc<ListAnalysesResponse>('ListAnalyses', {
        page: currentPage,
        pageSize: 10,
        query: currentQuery || undefined,
      });
      setAnalyses(resp.analyses || []);
      setTotalCount(resp.totalCount || 0);
      setPageSize(resp.pageSize || 10);
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested resource could not be found.' : (e.message || 'Failed to load analysis history'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(page, query);
  }, [page, query, loadData]);

  const handleSearch = useCallback((newQuery: string) => {
    const qp = new URLSearchParams();
    qp.set('page', '1');
    if (newQuery) qp.set('q', newQuery);
    navigate(`#/analysis/history?${qp.toString()}`);
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    const qp = new URLSearchParams();
    qp.set('page', String(newPage));
    if (query) qp.set('q', query);
    navigate(`#/analysis/history?${qp.toString()}`);
  }, [query, navigate]);

  const handleStartRename = (item: AnalysisRecord) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const handleSaveRename = async (id: string) => {
    if (!editName.trim() || savingRename) return;
    setSavingRename(true);
    try {
      const resp = await rpc<UpdateAnalysisResponse>('UpdateAnalysis', {
        id,
        name: editName.trim(),
      });
      setAnalyses(prev => prev.map(a => (a.id === id ? resp.analysis : a)));
      showToast(t('analysis.history.renameSuccess'), 'success');
      setEditingId(null);
    } catch (err: any) {
      console.error('Failed to rename analysis:', err);
      showToast(err.message || 'Failed to rename analysis', 'error');
    } finally {
      setSavingRename(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (deletingId) return;
    if (!window.confirm(t('analysis.history.deleteConfirm'))) return;

    setDeletingId(id);
    try {
      await rpc<DeleteAnalysisResponse>('DeleteAnalysis', { id });
      setAnalyses(prev => prev.filter(a => a.id !== id));
      setTotalCount(c => Math.max(0, c - 1));
      showToast(t('analysis.history.deleteSuccess'), 'success');
    } catch (err: any) {
      console.error('Failed to delete analysis:', err);
      showToast(err.message || 'Failed to delete analysis', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenAnalysis = async (item: AnalysisRecord) => {
    if (openingId) return;
    setOpeningId(item.id);
    try {
      if (item.id) {
        await rpc('GetAnalysis', { id: item.id });
      }
      const route = TYPE_ROUTES[item.type];
      const trackLabel = getAnalysisTrack(item);
      if (route) {
        try {
          sessionStorage.setItem('loaded_analysis_record', JSON.stringify(item));
        } catch {}
        navigate(`${route}/${encodeURIComponent(trackLabel)}`);
      }
    } catch (err: any) {
      const isSessionMissing = err?.status === 404 || err?.isNotFound || (err?.message && (err.message.includes('session') || err.message.includes('not found')));
      if (isSessionMissing) {
        setSessionRemovedAnalysis(item);
      } else {
        console.error('Failed to open analysis:', err);
        showToast(err.message || 'Failed to open analysis', 'error');
      }
    } finally {
      setOpeningId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / (pageSize || 10)));

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
          <History size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('analysis.history.title')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('analysis.history.subtitle')}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="mt-4 mb-8">
        <FilterBar
          initialQuery={query}
          schema={analysisSchema}
          onSearch={handleSearch}
          placeholder={t('analysis.history.searchPlaceholder')}
        />
      </div>

      {/* History Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} isNotFound={notFound} />
      ) : analyses.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="📊"
            message={t('analysis.history.empty')}
            description={t('analysis.history.emptyDesc')}
          />
        </div>
      ) : (
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="section-title">{t('analysis.history.title')}</h2>
            <span className="text-xs text-text-muted">{t('analysis.history.total', { n: totalCount })}</span>
          </div>

          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px] border-collapse">
                <thead>
                  <tr className="divider">
                    <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                      {t('analysis.history.table.type')}
                    </th>
                    <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                      {t('analysis.history.table.name')}
                    </th>
                    <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                      {t('analysis.history.table.track')}
                    </th>
                    <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                      {t('analysis.history.table.scope')}
                    </th>
                    <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                      {t('analysis.history.table.created')}
                    </th>
                    <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                      {t('analysis.history.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {analyses.map((item, idx) => {
                    const Icon = TYPE_ICONS[item.type] || BarChart2;
                    const isEditing = editingId === item.id;
                    const trackLabel = getAnalysisTrack(item);
                    const scopeLabel = getAnalysisScopeLabel(item, t);
                    const typeLabel = getTypeLabel(item.type, t);
                    const dateStr = item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString(undefined, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—';

                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-bg-hover transition-colors ${idx < analyses.length - 1 ? 'divider' : ''}`}
                      >
                        {/* Type Badge */}
                        <td className="py-2.5 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-md bg-accent/10 border border-accent/20 flex items-center justify-center text-accent shrink-0">
                              <Icon size={13} />
                            </div>
                            <span className="text-xs font-medium text-text-secondary">{typeLabel}</span>
                          </div>
                        </td>

                        {/* Name (Inline editable) */}
                        <td className="py-2.5 px-4">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5 max-w-sm">
                              <input
                                type="text"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveRename(item.id);
                                  if (e.key === 'Escape') setEditingId(null);
                                }}
                                autoFocus
                                disabled={savingRename}
                                className="px-2 py-1 rounded bg-bg-surface border border-accent text-xs font-medium text-text-primary focus:outline-none w-full"
                              />
                              <button
                                onClick={() => handleSaveRename(item.id)}
                                disabled={savingRename || !editName.trim()}
                                className="p-1 rounded bg-accent text-white hover:bg-accent-hover cursor-pointer"
                              >
                                <Check size={13} />
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="p-1 rounded text-text-muted hover:text-text-primary cursor-pointer"
                              >
                                <X size={13} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleOpenAnalysis(item)}
                              className="text-xs font-semibold text-text-primary hover:text-accent truncate text-left cursor-pointer transition-colors max-w-md block"
                            >
                              {item.name}
                            </button>
                          )}
                        </td>

                        {/* Track */}
                        <td className="py-2.5 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                            <MapPin size={12} className="text-text-muted shrink-0" />
                            <span className="truncate">{trackLabel || '—'}</span>
                          </div>
                        </td>

                        {/* Scope */}
                        <td className="py-2.5 px-4 whitespace-nowrap text-xs text-text-muted tabular-nums">
                          {scopeLabel}
                        </td>

                        {/* Created Date */}
                        <td className="py-2.5 px-4 whitespace-nowrap text-xs text-text-muted font-mono tabular-nums">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={12} className="text-text-muted shrink-0" />
                            <span>{dateStr}</span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="py-2.5 px-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleOpenAnalysis(item)}
                              title={t('analysis.history.open')}
                              className="p-1.5 rounded-lg text-text-muted hover:text-accent hover:bg-bg-subtle transition-colors cursor-pointer"
                            >
                              <ExternalLink size={14} />
                            </button>
                            <button
                              onClick={() => handleStartRename(item)}
                              title={t('analysis.history.rename')}
                              className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-subtle transition-colors cursor-pointer"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              disabled={deletingId === item.id}
                              title={t('analysis.history.delete')}
                              className="p-1.5 rounded-lg text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalCount}
          />
        </div>
      )}

      {sessionRemovedAnalysis && (
        <SessionRemovedAnalysisDialog
          open={!!sessionRemovedAnalysis}
          onClose={() => setSessionRemovedAnalysis(null)}
          analysis={sessionRemovedAnalysis}
          onDeleted={(deletedId) => {
            setAnalyses(prev => prev.filter(a => a.id !== deletedId));
            setTotalCount(c => Math.max(0, c - 1));
          }}
        />
      )}
    </div>
  );
}
